import { MenuMode, ToastMsgType } from '@app/engine/core/interfaces';
import { EventBus } from '@app/engine/events/event-bus';
import { EventBusEvent } from '@app/engine/events/event-bus-events';
import { html } from '@app/engine/utils/development/formatter';
import { errorManagerInstance } from '@app/engine/utils/errorManager';
import { getEl } from '@app/engine/utils/get-el';
import { isThisNode } from '@app/engine/utils/isThisNode';
import { keepTrackApi } from '@app/keepTrackApi';
import landscape3Png from '@public/img/icons/landscape3.png';
import { saveAs } from 'file-saver';
import { KeepTrackPlugin } from '../../engine/plugins/base-plugin';
import { SoundNames } from '../sounds/sounds';
export class ScenarioManagementPlugin extends KeepTrackPlugin {
    id = 'ScenarioManagementPlugin';
    dependencies_ = [];
    menuMode = [MenuMode.ADVANCED, MenuMode.SETTINGS, MenuMode.ALL];
    defaultScenarioName = 'My Scenario';
    defaultScenarioDescription = 'Description of My Scenario';
    bottomIconElementName = 'scenario-management-icon';
    bottomIconImg = landscape3Png;
    formPrefix_ = 'scenario-management-form';
    sideMenuElementName = 'scenario-management-menu';
    sideMenuElementHtml = html `
  <div id="scenario-management-menu" class="side-menu-parent start-hidden text-select">
    <div id="scenario-management-content" class="side-menu">
      <div class="row">
        <form id="${this.formPrefix_}-form">
          <div id="${this.formPrefix_}-general">
            <div class="row center"></div>
            </br>
            <div class="row center">
              <button id="${this.formPrefix_}-submit" class="btn btn-ui waves-effect waves-light" type="submit" name="action">Update Scenario &#9658;</button>
            </div>
            <h5 class="center-align">Scenario Settings</h5>
            <!-- Scenario Name -->
            <div class="input-field col s12">
              <input required value="${this.defaultScenarioName}" id="${this.formPrefix_}-name" type="text" kt-tooltip="The name of the scenario.">
              <label class="active" for="${this.formPrefix_}-name">Scenario Name</label>
            </div>
            <!-- Scenario Description -->
            <div class="input-field col s12">
              <input id="${this.formPrefix_}-description" type="text"
              value="${this.defaultScenarioDescription}"
              kt-tooltip="The description of the scenario." placeholder="Enter scenario description here...">
              <label class="active" for="${this.formPrefix_}-description">Description</label>
            </div>
            <!-- Scenario Start DateTime -->
            <div class="input-field col s12">
              <input id="${this.formPrefix_}-start-date" type="text"
                kt-tooltip="The start DTG of the scenario in UTC (YYYY-MM-DD HH:MM:SS.sss)." placeholder="YYYY-MM-DD HH:MM:SS.sss"
              >
              <label class="active" for="${this.formPrefix_}-start-date">Scenario Start</label>
            </div>
            <!-- Scenario End DateTime -->
            <div class="input-field col s12">
              <input id="${this.formPrefix_}-end-date" type="text"
                kt-tooltip="The end DTG of the scenario in UTC (YYYY-MM-DD HH:MM:SS.sss)." placeholder="YYYY-MM-DD HH:MM:SS.sss"
              >
              <label class="active" for="${this.formPrefix_}-end-date">Scenario End</label>
            </div>
          </div>
        </form>
        <div class="row center">
          <button id="${this.formPrefix_}-save" class="btn btn-ui waves-effect waves-light">Save Scenario &#9658;</button>
        </div>
        <div class="row center">
          <button id="${this.formPrefix_}-load" class="btn btn-ui waves-effect waves-light">Load Scenario &#9658;</button>
        </div>
      </div>
    </div>
  </div>`;
    isNotColorPickerInitialSetup = false;
    scenario = {
        name: this.defaultScenarioName,
        description: this.defaultScenarioDescription,
        startTime: null,
        endTime: null,
    };
    addHtml() {
        super.addHtml();
        EventBus.getInstance().on(EventBusEvent.uiManagerFinal, () => {
            getEl(`${this.formPrefix_}-start-date`)?.addEventListener('change', this.onDateChange_.bind(this));
            getEl(`${this.formPrefix_}-end-date`)?.addEventListener('change', this.onDateChange_.bind(this));
            getEl(`${this.formPrefix_}-form`)?.addEventListener('submit', this.onSubmit_.bind(this));
            getEl(`${this.formPrefix_}-save`)?.addEventListener('click', this.onSave_.bind(this));
            getEl(`${this.formPrefix_}-load`)?.addEventListener('click', this.onLoad_.bind(this));
        });
    }
    addJs() {
        super.addJs();
        EventBus.getInstance().on(EventBusEvent.calculateSimulationTime, (simulationTimeObj) => {
            let isOutsideBoundaries = false;
            if (this.scenario.startTime && simulationTimeObj.getTime() < this.scenario.startTime.getTime()) {
                simulationTimeObj.setTime(this.scenario.startTime.getTime());
                keepTrackApi.toast('Simulation time is before the scenario start time. Pausing at start time.', ToastMsgType.caution, true);
                isOutsideBoundaries = true;
            }
            if (this.scenario.endTime && simulationTimeObj.getTime() > this.scenario.endTime.getTime()) {
                simulationTimeObj.setTime(this.scenario.endTime.getTime());
                keepTrackApi.toast('Simulation time is after the scenario stop time. Pausing at stop time.', ToastMsgType.caution, true);
                isOutsideBoundaries = true;
            }
            if (isOutsideBoundaries) {
                const today = new Date();
                const timeManagerInstance = keepTrackApi.getTimeManager();
                timeManagerInstance.dynamicOffsetEpoch = Date.now();
                timeManagerInstance.staticOffset = timeManagerInstance.simulationTimeObj.getTime() - today.getTime();
                timeManagerInstance.changePropRate(0, false); // Pause without toast
                timeManagerInstance.synchronize();
                keepTrackApi.emit(EventBusEvent.staticOffsetChange, timeManagerInstance.staticOffset);
            }
        });
    }
    updateScenario(partialScenario) {
        if (!this.validateScenario_({
            name: partialScenario.name ?? this.scenario.name,
            description: partialScenario.description ?? this.scenario.description,
            startTime: partialScenario.startTime ?? this.scenario.startTime,
            endTime: partialScenario.endTime ?? this.scenario.endTime,
        })) {
            return false;
        }
        this.scenario = {
            ...this.scenario,
            ...partialScenario,
        };
        getEl(`${this.formPrefix_}-name`).value = this.scenario.name;
        getEl(`${this.formPrefix_}-description`).value = this.scenario.description;
        // If start time changes, update the dom element
        if (partialScenario.startTime) {
            const startDateInput = getEl(`${this.formPrefix_}-start-date`);
            if (startDateInput) {
                startDateInput.value = partialScenario.startTime
                    ? partialScenario.startTime.toISOString().replace('T', ' ').replace('Z', '')
                    : '';
            }
        }
        // If end time changes, update the dom element
        if (partialScenario.endTime) {
            const endDateInput = getEl(`${this.formPrefix_}-end-date`);
            if (endDateInput) {
                endDateInput.value = partialScenario.endTime
                    ? partialScenario.endTime.toISOString().replace('T', ' ').replace('Z', '')
                    : '';
            }
        }
        return true;
    }
    onSubmit_(e) {
        e?.preventDefault();
        const nameInput = getEl(`${this.formPrefix_}-name`);
        const descriptionInput = getEl(`${this.formPrefix_}-description`);
        const startDateInput = getEl(`${this.formPrefix_}-start-date`);
        const endDateInput = getEl(`${this.formPrefix_}-end-date`);
        const name = nameInput.value;
        const description = descriptionInput.value;
        const startDateStr = startDateInput.value;
        const endDateStr = endDateInput.value;
        if (!name) {
            errorManagerInstance.warn('Scenario Name is required.');
            return;
        }
        if (startDateStr && !this.validateDate_(startDateInput)) {
            errorManagerInstance.warn('Start Date is invalid.');
            return;
        }
        if (endDateStr && !this.validateDate_(endDateInput)) {
            errorManagerInstance.warn('End Date is invalid.');
            return;
        }
        // Here you would typically save these settings to your application's state or backend
        const newStartTime = startDateStr ? new Date(`${startDateStr}Z`) : null;
        const newEndTime = endDateStr ? new Date(`${endDateStr}Z`) : null;
        const isUpdateSuccess = this.updateScenario({
            name,
            description,
            startTime: newStartTime,
            endTime: newEndTime,
        });
        // Only show toast if button was clicked
        if (e && isUpdateSuccess) {
            keepTrackApi.toast('Scenario settings updated successfully!', ToastMsgType.normal);
        }
    }
    onDateChange_(e) {
        const input = e.target;
        this.validateDate_(input);
    }
    validateScenario_(scenario) {
        if (scenario.startTime && scenario.endTime && scenario.startTime >= scenario.endTime) {
            errorManagerInstance.warn('Scenario start time must be before end time.');
            return false;
        }
        // Must be valid dates or null
        if ((scenario.startTime && isNaN(scenario.startTime.getTime())) ||
            (scenario.endTime && isNaN(scenario.endTime.getTime()))) {
            errorManagerInstance.warn('Scenario start time and end time must be valid dates.');
            return false;
        }
        // if start or end then there must be both
        if ((scenario.startTime && !scenario.endTime) || (!scenario.startTime && scenario.endTime)) {
            errorManagerInstance.warn('Both scenario start time and end time must be set.');
            return false;
        }
        // Name must be less than 30 characters
        if (scenario.name.length > 30) {
            errorManagerInstance.warn('Scenario name must be less than 30 characters.');
            return false;
        }
        return true;
    }
    validateDate_(input) {
        const dateStr = input.value;
        // Simple regex to validate the format YYYY-MM-DD HH:MM:SS.sss
        const regex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d{1,3})?$/u;
        const isValid = regex.test(dateStr);
        if (!isValid) {
            // Show error message
            input.classList.remove('valid');
            input.classList.add('invalid');
        }
        else {
            // Remove error message
            input.classList.remove('invalid');
            input.classList.add('valid');
        }
        return isValid;
    }
    onSave_(evt) {
        this.onSubmit_();
        keepTrackApi.getSoundManager()?.play(SoundNames.MENU_BUTTON);
        const blob = new Blob([JSON.stringify(this.scenario, null, 2)], {
            type: 'text/plain;charset=utf-8',
        });
        try {
            saveAs(blob, `keeptrack-scenario-${this.scenario.name}.json`);
        }
        catch (e) {
            if (!isThisNode()) {
                errorManagerInstance.error(e, 'scenario-management.ts', 'Error saving scenario!');
            }
        }
        evt.preventDefault();
    }
    onLoad_() {
        keepTrackApi.getSoundManager()?.play(SoundNames.MENU_BUTTON);
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json,text/json';
        input.onchange = (e) => {
            const target = e.target;
            if (target.files && target.files.length > 0) {
                const file = target.files[0];
                const reader = new FileReader();
                reader.onload = (event) => {
                    if (event.target && typeof event.target.result === 'string') {
                        try {
                            const scenarioFile = JSON.parse(event.target.result);
                            const scenarioData = {
                                name: scenarioFile.name,
                                description: scenarioFile.description || '',
                                startTime: scenarioFile.startTime ? new Date(scenarioFile.startTime) : null,
                                endTime: scenarioFile.endTime ? new Date(scenarioFile.endTime) : null,
                            };
                            if (this.updateScenario(scenarioData)) {
                                keepTrackApi.toast('Scenario loaded successfully!', ToastMsgType.normal);
                            }
                        }
                        catch (error) {
                            errorManagerInstance.error(error, 'scenario-management.ts', 'Error loading scenario file!');
                        }
                    }
                };
                reader.readAsText(file);
            }
        };
        input.click();
    }
}
